import { Add } from '@mui/icons-material'
import {
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Typography
} from '@mui/material'
import { GreenText, SortableTableCell } from 'components'
import * as controllers from 'controllers'
import { VehicleDamageNoteDto } from 'dtos'
import { SortDirection } from 'localConstants/sortDirection'
import moment from 'moment'
import { enqueueSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import DamageNoteAddDialog from './DamageNoteAddDialog'

export default function RecallCampaignListCard() {
  const { vehicleGuid, damageGuid } = useParams()

  const [isGettingDamageNotes, setIsGettingDamageNotes] = useState<boolean>(false)
  const [vehicleDamageNotes, setVehicleDamageNotes] = useState<VehicleDamageNoteDto[]>([])
  const [totalCount, setTotalCount] = useState<number>(0)
  const [isDamageNoteSaving, setIsDamageNoteSaving] = useState<boolean>(false)
  const [damageNoteAddDialogOpen, setDamageNoteAddDialogOpen] = useState<boolean>(false)

  const [parameters, setParameters] = useState<{
    page: number
    pageSize: number
    sortBy: string
    sortDirection: SortDirection
    user?: string
    setDate?: string
    sentTo?: string
  }>({
    page: 0,
    pageSize: 5,
    sortBy: 'user',
    sortDirection: 'desc'
  })

  useEffect(() => {
    setIsGettingDamageNotes(true)
    controllers
      .getDamageNotes(vehicleGuid!, damageGuid!, parameters)
      .then(response => {
        setVehicleDamageNotes(response.value)
        setTotalCount(response.totalCount!)
      })
      .finally(() => {
        setIsGettingDamageNotes(false)
      })
  }, [vehicleGuid, damageGuid, parameters])

  const formatDate = (date: any) => {
    return moment(date).format('MM/DD/YYYY hh:mma')
  }

  const onChangeSortByDirection = (sortBy: string) => {
    setParameters(prev => ({
      ...parameters,
      sortBy,
      sortDirection: prev.sortBy === sortBy && prev.sortDirection === 'asc' ? 'desc' : 'asc'
    }))
  }

  return (
    <Card>
      <DamageNoteAddDialog
        initialValues={{}}
        isLoading={isDamageNoteSaving}
        onClose={() => setDamageNoteAddDialogOpen(false)}
        onSave={values => {
          setIsDamageNoteSaving(true)
          controllers
            .createVehicleDamageNote(values, vehicleGuid!, damageGuid!)
            .then(() => {
              enqueueSnackbar('Damage Note Saved Successfully!', { variant: 'success' })
              setDamageNoteAddDialogOpen(false)

              controllers.getDamageNotes(vehicleGuid!, damageGuid!, parameters).then(response => {
                setVehicleDamageNotes(response.value)
                setTotalCount(response.totalCount!)
              })
            })
            .finally(() => {
              setIsDamageNoteSaving(false)
            })
        }}
        open={damageNoteAddDialogOpen}
      />

      <CardContent>
        <Grid container spacing={1} alignItems='flex-start'>
          <Grid item xs={12} container alignItems='center' justifyContent='space-between'>
            <Grid item xs={12} sm={8}>
              <Typography fontWeight='bold' variant='h5'>
                Damage Notes
              </Typography>
            </Grid>
            <Grid item xs={12} sm='auto'>
              <Button
                onClick={() => {
                  setDamageNoteAddDialogOpen(true)
                }}
                variant='text'
                endIcon={<Add />}
              >
                ADD NOTE&nbsp;
              </Button>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            {isGettingDamageNotes && (
              <Grid item container alignItems='center' justifyContent='center' sx={{ my: 1 }}>
                <Grid>
                  <CircularProgress color='primary' size={50} />
                </Grid>
              </Grid>
            )}

            {!isGettingDamageNotes && (
              <Grid item xs={12}>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <SortableTableCell
                          onChangeSortByDirection={onChangeSortByDirection}
                          propertyName='timestamp'
                          sortBy={parameters.sortBy}
                          sortDirection={parameters.sortDirection}
                        >
                          Time Stamp
                        </SortableTableCell>

                        <SortableTableCell
                          onChangeSortByDirection={onChangeSortByDirection}
                          propertyName='enteredBy'
                          sortBy={parameters.sortBy}
                          sortDirection={parameters.sortDirection}
                        >
                          Entered By
                        </SortableTableCell>

                        <SortableTableCell
                          onChangeSortByDirection={onChangeSortByDirection}
                          propertyName='note'
                          sortBy={parameters.sortBy}
                          sortDirection={parameters.sortDirection}
                        >
                          Note
                        </SortableTableCell>

                        <TableCell></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {vehicleDamageNotes.map(damageNote => (
                        <TableRow key={damageNote.createdDateTime}>
                          <TableCell>{formatDate(damageNote.createdDateTime)}</TableCell>
                          <TableCell>
                            {damageNote.createdBy?.firstName} {damageNote.createdBy?.lastName}
                          </TableCell>
                          <TableCell>{damageNote.note}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                    <TableFooter>
                      <TableRow>
                        <TablePagination
                          rowsPerPageOptions={[5, 10, 25]}
                          SelectProps={{ fullWidth: false }}
                          count={totalCount}
                          rowsPerPage={parameters.pageSize}
                          page={parameters.page}
                          onPageChange={(_, page) => {
                            setParameters(prev => ({
                              ...prev,
                              page
                            }))
                          }}
                          onRowsPerPageChange={e => {
                            setParameters(prev => ({
                              ...prev,
                              page: 0,
                              pageSize: parseInt(e.target.value, 10)
                            }))
                          }}
                        />
                      </TableRow>
                    </TableFooter>
                  </Table>
                </TableContainer>
              </Grid>
            )}
          </Grid>

          <Grid item xs={12}>
            <GreenText variant='outlined'>
              {`**Add Note:** 
          
Clicking the Add Note button opens the Add Notes Dialog.

Notes are listed in ascending order based on Timestamp.
          
          `}
            </GreenText>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  )
}
